import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit2, Trash2, Save, X, Tag, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase, EntryType } from '../lib/supabase';

export default function EntryTypesManager() {
  const { t } = useTranslation();
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<EntryType | null>(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeCategory, setNewTypeCategory] = useState<'income' | 'expense'>('income');
  const [error, setError] = useState('');

  const translateEntryTypeName = (name: string, isDefault: boolean): string => {
    if (!isDefault) return name;
    const translationKey = `entryTypes.defaultTypes.${name}`;
    const translated = t(translationKey);
    return translated === translationKey ? name : translated;
  };

  useEffect(() => {
    loadEntryTypes();
  }, []);

  const loadEntryTypes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('entry_types')
      .select('*')
      .order('display_order');

    if (!error && data) {
      setEntryTypes(data);
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newTypeName.trim()) {
      setError('Entry type name is required');
      return;
    }

    setError('');
    const maxOrder = Math.max(...entryTypes.map(t => t.display_order), 0);

    const { error: insertError } = await supabase
      .from('entry_types')
      .insert({
        name: newTypeName.trim(),
        entry_category: newTypeCategory,
        is_default: false,
        is_deletable: true,
        display_order: maxOrder + 1
      });

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setNewTypeName('');
    setNewTypeCategory('income');
    setShowForm(false);
    loadEntryTypes();
  };

  const handleUpdate = async (id: string, newName: string, newCategory: 'income' | 'expense') => {
    if (!newName.trim()) {
      setError('Entry type name is required');
      return;
    }

    setError('');
    const { error: updateError } = await supabase
      .from('entry_types')
      .update({
        name: newName.trim(),
        entry_category: newCategory
      })
      .eq('id', id);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setEditingType(null);
    loadEntryTypes();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this entry type? This will affect all ledgers using it.')) {
      return;
    }

    const { error: deleteError } = await supabase
      .from('entry_types')
      .delete()
      .eq('id', id);

    if (deleteError) {
      setError(deleteError.message);
      return;
    }

    loadEntryTypes();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Tag className="w-6 h-6 text-emerald-600" />
            <h2 className="text-xl font-semibold text-slate-800">{t('entryTypes.title')}</h2>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('entryTypes.addType')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {showForm && (
          <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
            <div className="flex gap-3">
              <input
                type="text"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder={t('entryTypes.enterTypeName')}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 flex-1 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-white transition-colors">
                <input
                  type="radio"
                  name="newCategory"
                  value="income"
                  checked={newTypeCategory === 'income'}
                  onChange={(e) => setNewTypeCategory(e.target.value as 'income')}
                  className="text-emerald-600 focus:ring-emerald-500"
                />
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-slate-700">{t('entryTypes.income')}</span>
              </label>
              <label className="flex items-center gap-2 flex-1 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-white transition-colors">
                <input
                  type="radio"
                  name="newCategory"
                  value="expense"
                  checked={newTypeCategory === 'expense'}
                  onChange={(e) => setNewTypeCategory(e.target.value as 'expense')}
                  className="text-red-600 focus:ring-red-500"
                />
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-slate-700">{t('entryTypes.expense')}</span>
              </label>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {t('entryTypes.save')}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setNewTypeName('');
                  setNewTypeCategory('income');
                  setError('');
                }}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                {t('entryTypes.cancel')}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-600">Loading entry types...</div>
        ) : (
          <div className="space-y-3">
            {entryTypes.map((type) => (
              <div
                key={type.id}
                className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                {editingType?.id === type.id ? (
                  <div className="flex-1 space-y-3">
                    <input
                      type="text"
                      defaultValue={type.name}
                      id={`edit-name-${type.id}`}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                      autoFocus
                    />
                    <div className="flex gap-3">
                      <label className="flex items-center gap-2 flex-1 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-white transition-colors">
                        <input
                          type="radio"
                          name={`editCategory-${type.id}`}
                          value="income"
                          defaultChecked={type.entry_category === 'income'}
                          className="text-emerald-600 focus:ring-emerald-500"
                        />
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-slate-700">{t('entryTypes.income')}</span>
                      </label>
                      <label className="flex items-center gap-2 flex-1 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-white transition-colors">
                        <input
                          type="radio"
                          name={`editCategory-${type.id}`}
                          value="expense"
                          defaultChecked={type.entry_category === 'expense'}
                          className="text-red-600 focus:ring-red-500"
                        />
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-slate-700">{t('entryTypes.expense')}</span>
                      </label>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          const nameInput = document.getElementById(`edit-name-${type.id}`) as HTMLInputElement;
                          const categoryInput = document.querySelector(`input[name="editCategory-${type.id}"]:checked`) as HTMLInputElement;
                          handleUpdate(type.id, nameInput.value, categoryInput.value as 'income' | 'expense');
                        }}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingType(null)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        type.entry_category === 'income' ? 'bg-emerald-100' : 'bg-red-100'
                      }`}>
                        {type.entry_category === 'income' ? (
                          <TrendingUp className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 capitalize">{translateEntryTypeName(type.name, type.is_default)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                            type.entry_category === 'income'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {type.entry_category === 'income' ? t('entryTypes.income') : t('entryTypes.expense')}
                          </span>
                          {type.is_default && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                              {t('entryTypes.badges.default')}
                            </span>
                          )}
                          {!type.is_deletable && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded">
                              {t('entryTypes.badges.protected')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {type.is_deletable && (
                        <>
                          <button
                            onClick={() => setEditingType(type)}
                            className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(type.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">{t('entryTypes.aboutTitle')}</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• {t('entryTypes.aboutDefault')}</li>
          <li>• {t('entryTypes.aboutCustom')}</li>
          <li>• {t('entryTypes.aboutDeletion')}</li>
        </ul>
      </div>
    </div>
  );
}
